'use client';

import { TopLevelCommentItem } from './TopLevelCommentItem';
import { CommentForm } from './CommentForm';
import { ToneAndChildren, UserDetails } from '@/utils/helpers';
import { CommentWithUser } from './types';
import { createClient } from '@/utils/supabase/client';
import { useState } from 'react';
import Button from '@/components/ui/Button';
import { getToneCommentById, getToneComments } from '@/utils/supabase/queries';
import { Select } from '../Select/Select';

interface CommentsProps {
  tone: ToneAndChildren;
  comments: CommentWithUser[];
  userDetails: UserDetails | null;
  limit: number;
  totalCount?: number;
  isApiSelect?: boolean;
}

export function Comments({
  tone,
  comments,
  userDetails,
  totalCount = 0,
  limit,
  isApiSelect = false,
}: CommentsProps) {
  const supabase = createClient();
  const [_comments, setComments] = useState<CommentWithUser[]>(comments);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [sort, setSort] = useState('popular');
  const [hasMoreComments, setHasMoreComments] = useState(totalCount === limit);

  const onNewComment = async (id: number) => {
    setIsLoadingMore(true);
    const newComment = await getToneCommentById(supabase, id);
    if (newComment) {
      setComments((prev) => [newComment, ...prev]);
    }
    setIsLoadingMore(false);
  };

  const onCommentReload = async (id: number) => {
    const comment = await getToneCommentById(supabase, id);
    // update the displayedComments
    if (comment) {
      setComments((prev) => prev.map((c) => (c.id === id ? comment : c)));
    } else {
      // remove the comment from the displayedComments
      setComments((prev) => prev.filter((c) => c.id !== id));
    }
  };

  const loadMoreComments = async () => {
    if (isLoadingMore || !hasMoreComments) return;
    setIsLoadingMore(true);
    try {
      const newComments = await getToneComments(supabase, tone.id!, limit, _comments.length, sort);
      setComments((prev) => [...prev, ...newComments]);
      // Check if there are more comments to load
      setHasMoreComments(newComments.length === limit);
    } catch (error) {
      console.error('Error loading more comments:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleSortChange = async (value: string) => {
    setSort(value);
    const newComments = await getToneComments(supabase, tone.id!, limit, 0, value);
    setComments(newComments);
    setHasMoreComments(newComments.length === limit);
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-10">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-xl">
            {totalCount === 0
              ? 'Comments'
              : totalCount === 1
                ? '1 Comment'
                : `${totalCount.toLocaleString()} Comments`}
          </h3>
          <div className="flex justify-end items-center min-w-[125px] sm:min-w-[150px] z-3">
            <Select
              options={[
                { label: 'Popular', value: 'popular' },
                { label: 'Newest', value: 'newest' },
                { label: 'Oldest', value: 'oldest' },
              ]}
              value={sort}
              onChange={(value) => handleSortChange(value as string)}
            />
          </div>
        </div>

        <CommentForm
          toneId={tone.id!}
          userDetails={userDetails}
          parentId={null}
          onSubmit={onNewComment}
        />
      </div>

      <div className="flex flex-col gap-10">
        {_comments.length === 0 ? (
          <></>
        ) : (
          _comments.map((comment) => (
            <TopLevelCommentItem
              key={comment.id}
              supabase={supabase}
              comment={comment}
              userDetails={userDetails}
              toneId={tone.id!}
              toneUsername={tone.user.username!}
              onReload={onCommentReload}
              limit={limit}
              isApiSelect={isApiSelect}
            />
          ))
        )}
      </div>

      {/* Load More Button */}
      {hasMoreComments && (
        <div className="flex justify-center">
          <Button onClick={loadMoreComments} disabled={isLoadingMore} variant="slim" isSecondary>
            {isLoadingMore ? 'Loading...' : 'View More'}
          </Button>
        </div>
      )}
    </div>
  );
}
